import { createFileRoute } from "@tanstack/react-router";
import { useSyncExternalStore } from "react";
import { Activity, ShieldCheck, ToggleRight, AlertTriangle } from "lucide-react";
import {
  listSubsystems,
  useSubsystem,
  getRemoteFlagKey,
  type SubsystemKey,
} from "@/lib/pr36b/subsystems";
import {
  getBreakerSnapshot,
  isDegradedMode,
  subscribeDegraded,
} from "@/lib/pr36b/breakers";
import { subscribeBreaker } from "@/lib/resilience/circuitBreaker";
import { useEdgeSafeMode } from "@/lib/resilience/edgeSafeMode";

export const Route = createFileRoute("/admin/resilience")({
  component: ResiliencePage,
  head: () => ({ meta: [{ title: "Resilience · VerseSport" }] }),
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">
      No se pudo cargar el panel: {error.message}
    </div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm">No encontrado.</div>,
});

function useBreakers() {
  return useSyncExternalStore(
    (cb) => subscribeBreaker(cb),
    () => getBreakerSnapshot(),
    () => [] as ReturnType<typeof getBreakerSnapshot>,
  );
}
function useDegraded() {
  return useSyncExternalStore(
    (cb) => subscribeDegraded(cb),
    () => isDegradedMode(),
    () => false,
  );
}

function stateClass(state: string) {
  if (state === "open") return "bg-red-500/15 text-red-500 border-red-500/40";
  if (state === "half_open") return "bg-amber-500/15 text-amber-500 border-amber-500/40";
  return "bg-emerald-500/15 text-emerald-500 border-emerald-500/40";
}

function ResiliencePage() {
  const breakers = useBreakers();
  const degraded = useDegraded();
  const edge = useEdgeSafeMode();
  const subsystems = listSubsystems();

  return (
    <main className="mx-auto max-w-5xl space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-black tracking-tight">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Resilience · PR-36B
        </h1>
        <p className="text-sm text-muted-foreground">
          Vista read-only del estado operacional: feature flags por subsistema,
          circuit breakers activos y modo degradado.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Modo degradado"
          value={degraded ? "ACTIVO" : "estable"}
          tone={degraded ? "warn" : "ok"}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
        <StatCard
          label="Edge safe level"
          value={edge.level}
          tone={edge.level === "normal" ? "ok" : edge.level === "soft" ? "warn" : "bad"}
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Circuits open"
          value={String(breakers.filter((b) => b.state === "open").length)}
          tone={breakers.some((b) => b.state === "open") ? "warn" : "ok"}
          icon={<ShieldCheck className="h-4 w-4" />}
        />
      </section>

      <section className="rounded-xl border border-border bg-background/60 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-black">
          <ToggleRight className="h-4 w-4" /> Subsistemas
        </h2>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {subsystems.map((s) => (
            <SubsystemRow key={s} k={s} />
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-border bg-background/60 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-black">
          <Activity className="h-4 w-4" /> Circuit breakers
        </h2>
        {breakers.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sin actividad aún.</p>
        ) : (
          <ul className="space-y-1.5 text-[11px]">
            {breakers.map((b) => (
              <li
                key={b.key}
                className="flex flex-col gap-1 rounded border border-border bg-background/40 p-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <code className="font-bold">{b.key}</code>
                <div className="flex items-center gap-2">
                  <span className={`rounded border px-1.5 py-0.5 font-black ${stateClass(b.state)}`}>
                    {b.state.toUpperCase()}
                  </span>
                  <span className="text-muted-foreground">
                    fails={b.failures} · ok={b.successes}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function StatCard({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "bad";
  icon: React.ReactNode;
}) {
  const cls =
    tone === "ok"
      ? "border-emerald-500/40 text-emerald-500"
      : tone === "warn"
      ? "border-amber-500/40 text-amber-500"
      : "border-red-500/40 text-red-500";
  return (
    <div className={`rounded-xl border ${cls} bg-background/60 p-4`}>
      <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function SubsystemRow({ k }: { k: SubsystemKey }) {
  const enabled = useSubsystem(k);
  return (
    <li className="flex items-center justify-between rounded border border-border bg-background/40 px-3 py-2 text-[12px]">
      <div className="flex flex-col">
        <span className="font-black">{k}</span>
        <code className="text-[10px] text-muted-foreground">{getRemoteFlagKey(k)}</code>
      </div>
      <span
        className={`rounded border px-1.5 py-0.5 text-[10px] font-black ${
          enabled
            ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-500"
            : "border-red-500/40 bg-red-500/15 text-red-500"
        }`}
      >
        {enabled ? "ENABLED" : "DISABLED"}
      </span>
    </li>
  );
}
